import { useRef, useState } from "react";
import { api, API, auth } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Upload, Loader2, X, Sparkles } from "lucide-react";
import { toast } from "sonner";

/**
 * Reusable image upload widget.
 * Props:
 *  - value: storage_path string (e.g. "medlearn/uploads/...")
 *  - onChange(path): called with the storage path on success (or "" on remove)
 *  - aspect: tailwind aspect class, e.g. "aspect-[16/9]"
 *  - aiHint?: optional prompt to enable "Generate with AI" button
 *  - testid?: data-testid prefix
 */
export default function ImageUpload({ value, onChange, aspect = "aspect-[16/9]", aiHint, testid = "img" }) {
  const ref = useRef(null);
  const [loading, setLoading] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);

  const url = value ? `${API}/files/${value}?auth=${auth.token()}` : "";

  async function onFile(e) {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) { toast.error("Max 5MB"); return; }
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const r = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" }});
      onChange(r.data.path);
      toast.success("Uploaded");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally { setLoading(false); if (ref.current) ref.current.value = ""; }
  }

  async function genAI() {
    if (!aiHint) return;
    setAiLoading(true);
    try {
      const startR = await api.post("/jobs/start", { type: "image_generate", payload: { prompt: aiHint }});
      const jobId = startR.data.job_id;
      let result = null;
      for (let i = 0; i < 60; i++) {
        await new Promise(r => setTimeout(r, 3000));
        const jr = await api.get(`/jobs/${jobId}`);
        if (jr.data.status === "done") { result = jr.data.result; break; }
        if (jr.data.status === "error") throw new Error(jr.data.error || "Generation failed");
      }
      if (!result) throw new Error("AI image timed out");
      onChange(result.path);
      toast.success("AI banner generated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "AI image failed");
    } finally { setAiLoading(false); }
  }

  return (
    <div className="space-y-2">
      <div className={`${aspect} rounded-2xl overflow-hidden border-2 border-dashed border-slate-200 bg-slate-50 relative group`}>
        {value ? (
          <>
            <img src={url} alt="" className="w-full h-full object-contain" />
            <button onClick={() => onChange("")} className="absolute top-2 right-2 h-7 w-7 rounded-full bg-white/90 grid place-items-center hover:bg-white shadow" data-testid={`${testid}-remove`}>
              <X className="h-4 w-4 text-slate-700"/>
            </button>
          </>
        ) : (
          <div className="absolute inset-0 grid place-items-center text-slate-400">
            <div className="text-center">
              <Upload className="h-7 w-7 mx-auto mb-1 opacity-50"/>
              <div className="text-xs">PNG, JPG, WEBP up to 5MB</div>
            </div>
          </div>
        )}
      </div>
      <div className="flex gap-2">
        <input ref={ref} type="file" accept="image/*" className="hidden" onChange={onFile} data-testid={`${testid}-input`}/>
        <Button variant="outline" size="sm" type="button" onClick={() => ref.current?.click()} disabled={loading} className="flex-1" data-testid={`${testid}-upload`}>
          {loading ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin"/> : <Upload className="h-4 w-4 mr-1.5"/>}
          Upload Image
        </Button>
        {aiHint && (
          <Button variant="outline" size="sm" type="button" onClick={genAI} disabled={aiLoading} className="text-sky-700 border-sky-300" data-testid={`${testid}-ai`}>
            {aiLoading ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin"/> : <Sparkles className="h-4 w-4 mr-1.5"/>}
            AI Banner
          </Button>
        )}
      </div>
    </div>
  );
}
